/**
 * UploadProgress Component
 * Displays file upload progress with cancel option
 */
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';

export interface UploadProgressProps {
  /** Name of the file being uploaded */
  filename: string;
  /** Upload progress (0-100), null for indeterminate */
  progress: number | null;
  /** Current upload stage description */
  stage?: 'initializing' | 'uploading' | 'processing';
  /** Whether cancel is available */
  canCancel?: boolean;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
  /** Custom class name */
  className?: string;
}

/**
 * File upload progress indicator with animated progress bar.
 */
export function UploadProgress({
  filename,
  progress,
  stage = 'uploading',
  canCancel = true,
  onCancel,
  className,
}: UploadProgressProps) {
  const isIndeterminate = progress === null;
  const clampedProgress = progress !== null ? Math.min(100, Math.max(0, progress)) : 0;

  const stageLabels: Record<string, string> = {
    initializing: 'Preparing upload...',
    uploading: 'Uploading...',
    processing: 'Processing document...',
  };

  return (
    <div
      className={cn('border border-gray-200 rounded-lg p-6', className)}
      role="region"
      aria-label="Upload progress"
      aria-live="polite"
    >
      {/* File info */}
      <div className="flex items-center gap-3">
        <FileIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{filename}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-600">{stageLabels[stage]}</span>
          {!isIndeterminate && (
            <span className="text-sm font-medium text-gray-900">{clampedProgress}%</span>
          )}
        </div>
        <div
          className="h-2 bg-gray-100 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={isIndeterminate ? undefined : clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Upload progress: ${isIndeterminate ? 'in progress' : `${clampedProgress}%`}`}
        >
          {isIndeterminate ? (
            // Indeterminate animated progress bar
            <div className="h-full w-1/3 bg-primary-500 rounded-full animate-indeterminate-progress" />
          ) : (
            // Determinate progress bar
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${clampedProgress}%` }}
            />
          )}
        </div>
      </div>

      {/* Cancel button */}
      {canCancel && onCancel && (
        <div className="mt-4">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel Upload
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * File icon component
 */
function FileIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

export default UploadProgress;
